import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { api, eur, errMsg } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const FIELDS = [
  ["name", "Nome e cognome", "col-span-2"],
  ["email", "Email", "col-span-2 md:col-span-1"],
  ["phone", "Telefono", "col-span-2 md:col-span-1"],
  ["address", "Indirizzo (via e numero civico)", "col-span-2"],
  ["city", "Città", "col-span-2 md:col-span-1"],
  ["zip", "CAP", "col-span-1 md:col-span-1"],
  ["country", "Paese", "col-span-1 md:col-span-2"],
];

export default function Checkout() {
  const { items, total } = useCart();
  const [f, setF] = useState({ name: "", email: "", phone: "", address: "", city: "", zip: "", country: "Italia" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/checkout", {
        customer: f,
        items: items.map((i) => ({ product_id: i.id, qty: i.qty })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (er) {
      toast.error(errMsg(er));
      setBusy(false);
    }
  };

  if (!items.length)
    return (
      <main className="max-w-[1400px] mx-auto px-5 md:px-10 py-16" data-testid="checkout-empty">
        <p>Il carrello è vuoto. <Link to="/" className="underline hover:text-[#FF3333]">Torna alle offerte</Link></p>
      </main>
    );

  return (
    <main className="max-w-[1400px] mx-auto px-5 md:px-10 py-10" data-testid="checkout-page">
      <span className="tag text-[#555]">Checkout ospite</span>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-1 mb-10">Completa l'ordine</h1>
      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-10">
          <section>
            <h2 className="tag mb-4">01 — Indirizzo di consegna</h2>
            <div className="grid grid-cols-2 gap-4">
              {FIELDS.map(([k, label, cls]) => (
                <label key={k} className={`${cls} block`}>
                  <span className="text-sm block mb-1.5">{label}</span>
                  <input
                    data-testid={`checkout-${k}-input`}
                    required={k !== "phone"}
                    readOnly={k === "country"}
                    type={k === "email" ? "email" : "text"}
                    value={f[k]}
                    onChange={(e) => setF({ ...f, [k]: e.target.value })}
                    className="field"
                  />
                </label>
              ))}
            </div>
          </section>
          <section>
            <h2 className="tag mb-4">02 — Pagamento</h2>
            <div className="border border-[#0F0F0F] bg-white p-4 flex items-center gap-3 text-sm" data-testid="checkout-stripe-info">
              <Lock size={18} strokeWidth={1.5} />
              Pagamento sicuro con carta tramite Stripe. Verrai reindirizzato alla pagina di pagamento.
            </div>
          </section>
        </div>
        <aside className="lg:col-span-5">
          <div className="border border-[#0F0F0F] bg-white sticky top-24">
            <h2 className="tag p-5 border-b border-[#0F0F0F]">Riepilogo</h2>
            <div className="divide-y divide-[#0F0F0F]/20">
              {items.map((i) => (
                <div key={i.id} className="p-4 flex gap-3 items-center">
                  <img src={i.image} alt="" className="w-14 h-14 object-cover border border-[#0F0F0F]" />
                  <p className="flex-1 text-sm line-clamp-2">{i.title} <span className="font-mono text-[#555]">× {i.qty}</span></p>
                  <span className="font-mono text-sm">{eur(i.price * i.qty)}</span>
                </div>
              ))}
            </div>
            <div className="p-5 border-t border-[#0F0F0F] space-y-4">
              <div className="flex justify-between text-sm"><span>Spedizione</span><span className="font-mono">Gratuita</span></div>
              <div className="flex justify-between items-end">
                <span className="tag">Totale</span>
                <span className="font-display font-black text-4xl tracking-tight" data-testid="checkout-total">{eur(total)}</span>
              </div>
              <button data-testid="checkout-submit-button" disabled={busy} className="btn-primary w-full h-12 font-medium">
                {busy ? "Elaborazione..." : `Paga ${eur(total)}`}
              </button>
              <p className="text-xs text-[#555]" data-testid="checkout-legal-note">
                Pagando accetti le <a href="/condizioni-vendita" target="_blank" rel="noreferrer" className="underline">Condizioni di vendita</a> e
                dichiari di aver letto l'<a href="/privacy" target="_blank" rel="noreferrer" className="underline">Informativa privacy</a> e
                il <a href="/recesso" target="_blank" rel="noreferrer" className="underline">Diritto di recesso</a>.
              </p>
            </div>
          </div>
        </aside>
      </form>
    </main>
  );
}
